import React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import "./NavBar.scss";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../../slices/userSlice";

const logoPath = "/AP.jpg";

export default function NavBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userMenuAnchorElement, setUserMenuAnchorElement] = React.useState(null);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const isUserMenuOpen = Boolean(userMenuAnchorElement);

  const isLoggedIn = useSelector((state) => state.user.isAuthenticated);

  const handleOpenUserMenu = (event) => {
    setUserMenuAnchorElement(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchorElement(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const go = (path, requireAuth = true) => {
    if (requireAuth && !isLoggedIn) {
      setSnackbarOpen(true);
      navigate("/login");
    } else {
      navigate(path);
    }
  };

  const handleRedirectProfile = () => {
    handleCloseUserMenu();
    go("/ProfilePage");
  };

  const handleRedirectLogin = () => {
    handleCloseUserMenu();
    if (isLoggedIn) dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <>
      <header className="ap-nav" data-testid="nav-bar">
        {isLoggedIn && (
          <nav className="ap-nav__grid" aria-label="Primary Navigation" data-testid="nav-links">
            <a
              href="/"
              className="ap-link"
              data-testid="nav-home-link"
              onClick={(e) => {
                e.preventDefault();
                go("/");
              }}
            >
              Home
            </a>
          </nav>
        )}

        <div className="ap-nav__logo" data-testid="nav-logo">
          <a
            href="https://www.accesspointprogram.org/"
            aria-label="Access Point Website"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-logo-link"
          >
            <img src={logoPath} alt="Access Point Logo" className="ap-logo" data-testid="nav-logo-image"/>
          </a>
        </div>


        {isLoggedIn && (
          <div className="ap-nav__actions" data-testid="nav-user-menu">
            <IconButton
              aria-label="Open user menu"
              aria-controls={isUserMenuOpen ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen ? "true" : undefined}
              onClick={handleOpenUserMenu}
              size="large"
              data-testid="nav-user-menu-button"
            >
              <MenuIcon />
            </IconButton>

            <Menu
              id="user-menu"
              data-testid="nav-user-menu"
              anchorEl={userMenuAnchorElement}
              open={isUserMenuOpen}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{
                "& .MuiMenuItem-root": {
                  fontFamily: "'Poppins', sans-serif",
                },
                "& .MuiTypography-root": {
                  fontFamily: "'Poppins', sans-serif",
                },
              }}
            >
              <MenuItem onClick={handleRedirectProfile} data-testid="nav-profile-menu-item">
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleRedirectLogin} data-testid="nav-logout-menu-item">
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Log out</ListItemText>
              </MenuItem>
            </Menu>
          </div>
        )}
      </header>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        data-testid="nav-snackbar"
      >
        <Alert onClose={handleCloseSnackbar} severity="warning" sx={{ width: "100%" }} data-testid="nav-snackbar-alert">
          You must be logged in to access this page.
        </Alert>
      </Snackbar>
    </>
  );
}
